import { getIsSuperuserFromAccessToken } from './jwt';
export const financeAllowedPaths = new Set<string>([
    '/',
    '/dashboard',
    '/deposit-history',
    '/users',
    '/payments',
    '/payments/manual',
    '/payments/api',
    //'/referral',
    //'/referred-users',
    // Wallet sub-pages
    '/withdraw-request',
    '/add-fund',
    '/deposit-request',
'/deposit-analytics',
    // Sales Report
    '/sales-report',
    '/sales-report/data',
    // Bank Details
    '/bank-details',
    // General Settings
    '/general-settings',
]);


export const rollCallerAllowedPaths = new Set([
    '/',
    '/dashboard',

    // Operations
    '/players',

    // Starline
    '/starline/market',
    '/starline/bet-history',

    // Wallet
    '/withdraw-request',
    '/add-fund',
    '/deposit-request',
]);



export const supportAllowedPaths = new Set<string>([
    '/',
    '/dashboard',
   // '/bet-history',
    '/withdraw-history',
    '/deposit-history',
    //'/referral',
    '/referred-users',
    '/change-password',
]);

export const agentInternalAllowedPaths = new Set<string>([
    '/',
    '/dashboard',
    '/referred-users',
    '/referral',
    '/create-user',
    
]);

export const superadminAllowedPaths = new Set<string>([
    '/',
    '/dashboard',
    // App Configuration and App Customization only
    '/app-configurations',
    '/app-customization',
    // Users
    '/users',
'/deposit-analytics',
    // Manage Apps
    '/manage-apps',
]);

const publicPaths = new Set<string>([
    '/login',
    '/logout',
]);

export function hasAccess(path: string, role?: string | null) {
    // Normalize path to only the first segment (ignore query and deeper paths)
    try {
        const url = new URL(path, window.location.origin);
        path = url.pathname;
    } catch {
        // path may already be a pathname
    }
    
    // Always allow public paths
    if (publicPaths.has(path)) return true;
    
    // If role isn't loaded yet, only allow public paths
    if (!role) {
        return publicPaths.has(path);
    }
    
    const roleLower = role.toLowerCase();
    
    // Extract role from URL if present
    const pathSegments = path.split('/').filter(Boolean);
    let cleanPath = path;
    let urlRole = null;
    
    if (pathSegments.length > 0) {
        const firstSegment = pathSegments[0];
        if (['agentinternal', 'finance', 'support', 'subadmin'].includes(firstSegment)) {
            if (firstSegment === 'agentinternal') {
                urlRole = 'agent (internal)';
            } else if (firstSegment === 'subadmin') {
                urlRole = 'sub_admin';
            } else {
                urlRole = firstSegment;
            }
            cleanPath = '/' + pathSegments.slice(1).join('/');
            if (cleanPath === '/') cleanPath = '/dashboard';
        }
    }
    
    // Check if URL role matches user role
    if (urlRole && urlRole !== roleLower) {
        console.log(`URL role (${urlRole}) doesn't match user role (${roleLower})`);
        return false;
    }
    
    console.log(`hasAccess - Role: ${role}, Role lowercased: ${roleLower}, Path: ${path}, Clean path: ${cleanPath}`);
    
    // Player App Status - restricted route
    // Backend independently enforces administrator and app scope.
    if (cleanPath === '/player-app-status') {
        const isAdmin = roleLower === 'admin' &&
            getIsSuperuserFromAccessToken();

        const isSubAdmin = [
            'sub_admin',
            'sub admin',
            'subadmin'
        ].includes(roleLower);

        return isAdmin || isSubAdmin;
    }

    // Admin role has full access, but Manage Apps is restricted
    // to a real Django superuser using the JWT is_superuser claim.
    if (roleLower === 'admin') {
        if (cleanPath === '/manage-apps') {
            const isSuperuser = getIsSuperuserFromAccessToken();

            console.log(
                `Admin role - manage-apps superuser check: ${isSuperuser}`
            );

            return isSuperuser;
        }

        console.log('Admin role - full access');
        return true;
    }
    
    // Sub Admin role has same access as Admin (full access except manage-apps and role-creation)
    // Handle variations: "Sub Admin", "SubAdmin", "sub admin", "subadmin", "sub_admin"
    if (roleLower === 'sub admin' || roleLower === 'subadmin' || roleLower === 'sub_admin' || (roleLower.includes('sub') && roleLower.includes('admin') && roleLower !== 'superadmin')) {
        if (cleanPath === '/manage-apps') {
            console.log('Sub Admin role - manage-apps is Super Admin only');
            return false;
        }
        if (cleanPath === '/role-creation') {
            console.log('Sub Admin role - role-creation is not allowed');
            return false;
        }
        console.log('Sub Admin role - full access (except manage-apps and role-creation)');
        return true;
    }
    
    // Agent (internal) role has limited access
    if (roleLower === 'agent (internal)') {
        const hasAccessToPath = agentInternalAllowedPaths.has(cleanPath);
        console.log(`Agent (internal) role - checking path ${cleanPath}: ${hasAccessToPath}`);
        console.log('Agent (internal) allowed paths:', Array.from(agentInternalAllowedPaths));
        return hasAccessToPath;
    }
    
    // Other Agent roles have full access
    if (roleLower.includes('agent') && roleLower !== 'agent (internal)') {
        console.log('Other Agent role - full access');
        return true;
    }
    
    // Roll Caller has tightly restricted dashboard access
    if (
        roleLower === 'roll caller' ||
        roleLower === 'roll_caller'
    ) {
        const isPlayerDetail =
            cleanPath.startsWith('/players/') &&
            cleanPath !== '/players';

        const hasAccessToPath =
            rollCallerAllowedPaths.has(cleanPath) ||
            isPlayerDetail;

        console.log(`Roll Caller role - checking path ${cleanPath}: ${hasAccessToPath}`);
        return hasAccessToPath;
    }

    // Finance role can only access specific paths
    if (roleLower === 'finance') {
        const hasAccessToPath = financeAllowedPaths.has(cleanPath);
        console.log(`Finance role - checking path ${cleanPath}: ${hasAccessToPath}`);
        console.log('Finance allowed paths:', Array.from(financeAllowedPaths));
        return hasAccessToPath;
    }
    
    // Support role limited access
    if (roleLower === 'support') {
        return supportAllowedPaths.has(cleanPath);
    }
    
    // User role has limited access (everything except finance-only pages)
    if (roleLower === 'user') {
        const hasAccessToPath = !financeAllowedPaths.has(cleanPath);
        console.log(`User role - checking path ${cleanPath}: ${hasAccessToPath}`);
        return hasAccessToPath;
    }
    
    // Super Admin role has access only to Settings and Users paths
    if (roleLower === 'superadmin') {
        const hasAccessToPath = superadminAllowedPaths.has(cleanPath);
        console.log(`Super Admin role - checking path ${cleanPath}: ${hasAccessToPath}`);
        console.log('Super Admin allowed paths:', Array.from(superadminAllowedPaths));
        return hasAccessToPath;
    }
    
    // Default: no access for unknown roles
    console.log('Unknown role - no access');
    return false;
}


